import { useMemo, useState } from 'react';
import { errorText } from '@/shared/api';
import { toast } from '@/shared/lib';
import { Dialog } from '@/shared/ui';
import { useGetGuideQuery, useGetMeQuery, useSaveGuideMutation } from '../api/guide';
import { addGuide, MAX_GUIDES, moveGuide, removeGuide, tidy } from '../model/edit';
import { GUIDES, withLinks, type Guide } from '../model/steps';
import { GuideEditor } from './GuideEditor';
import { GuideView } from './GuideView';

// "How to print": a button that opens the step-by-step guide for the Brother label printer, with a tab for Adobe Reader (the
// easy way, with a link to the free download) and one for Chrome. `className` styles the button for where it sits.
// The guide comes from the server once a Super Admin has saved it (otherwise the built-in one in model/steps.ts); a Super
// Admin signed in through the Toolkit gets "Edit guide".
export function PrintHelpButton({ className = 'btn ghost', label = 'How to print' }: { className?: string; label?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={className} onClick={() => setOpen(true)}>{label}</button>
      {open && <PrintHelpDialog onClose={() => setOpen(false)} />}
    </>
  );
}

function PrintHelpDialog({ onClose }: { onClose: () => void }) {
  const saved = useGetGuideQuery();
  const me = useGetMeQuery();
  const [save, saving] = useSaveGuideMutation();
  const guides = useMemo(() => (saved.data?.guides ? withLinks(saved.data.guides) : GUIDES), [saved.data]);
  const loading = saved.isLoading;

  const [tab, setTab] = useState<string>(GUIDES[0]!.id);
  const [draft, setDraft] = useState<Guide[] | null>(null);
  const shown = draft ?? guides;
  const guide = shown.find((g) => g.id === tab) ?? shown[0]!;
  const dirty = !!draft && JSON.stringify(draft) !== JSON.stringify(guides);

  const close = () => { if (dirty && !window.confirm('Close without saving your changes to the guide?')) return; onClose(); };
  const cancel = () => { if (dirty && !window.confirm('Throw away your changes to the guide?')) return; setDraft(null); };
  const submit = async () => {
    if (!draft) return;
    try {
      await save({ rev: saved.data?.rev ?? 0, guides: draft.map(tidy) }).unwrap();
      setDraft(null);
      toast('Guide saved ✓ Everyone sees it now.');
    } catch (e) { toast(errorText(e, 'The guide could not be saved.'), true); }
  };

  const loginUrl = me.data ? `${me.data.toolkitUrl}/login.html?next=${encodeURIComponent(window.location.href)}` : '';
  const actions = draft ? (
    <>
      <button type="button" className="btn ghost" onClick={cancel}>Cancel</button>
      <button type="button" className="btn" disabled={saving.isLoading} onClick={submit}>{saving.isLoading ? 'Saving…' : 'Save guide'}</button>
    </>
  ) : (
    <>
      {me.data?.superAdmin && <button type="button" className="btn ghost" disabled={loading} onClick={() => setDraft(structuredClone(guides))}>✎ Edit guide</button>}
      {me.data && !me.data.signedIn && <a className="btn ghost help-signin" href={loginUrl}>Super Admin sign-in</a>}
      <button type="button" className="btn" onClick={onClose}>Got it</button>
    </>
  );

  return (
    <Dialog title={draft ? 'Edit: How to print price stickers' : 'How to print price stickers'} wide onClose={close} actions={actions}>
      {draft
        ? <p className="hint" style={{ marginTop: 0, marginBottom: 18 }}>Only Super Admins see this. Changes show for everyone after <b>Save guide</b>.{me.data && !me.data.canSave && <b> Saving is not set up on this server yet.</b>}</p>
        : <p className="hint" style={{ marginTop: 0, marginBottom: 18 }}>For the Brother label printer with the 62 mm roll. Download the PDF first, then follow one of these.</p>}
      <div className="card-tabs" role="group" aria-label="Ways to print">
        {shown.map((g) => (
          <button key={g.id} type="button" aria-pressed={g.id === guide.id} className={g.id === guide.id ? 'active' : ''} onClick={() => setTab(g.id)}>{g.tab || 'Untitled'}</button>
        ))}
      </div>
      {draft && (
        <div className="guide-tools" role="group" aria-label="Guides">
          <button type="button" className="btn ghost sm" disabled={draft.length >= MAX_GUIDES}
            onClick={() => { const { list, id } = addGuide(draft); setDraft(list); setTab(id); }}>+ Add a guide</button>
          <button type="button" className="btn ghost sm" aria-label={`← Move left: ${guide.tab || 'Untitled'}`} disabled={draft[0]?.id === guide.id}
            onClick={() => setDraft(moveGuide(draft, guide.id, -1))}>← Move left</button>
          <button type="button" className="btn ghost sm" aria-label={`Move right →: ${guide.tab || 'Untitled'}`} disabled={draft[draft.length - 1]?.id === guide.id}
            onClick={() => setDraft(moveGuide(draft, guide.id, 1))}>Move right →</button>
          <button type="button" className="btn ghost sm danger" aria-label={`Delete this guide: ${guide.tab || 'Untitled'}`} disabled={draft.length === 1}
            onClick={() => {
              if (!window.confirm(`Delete the "${guide.tab || 'Untitled'}" guide with its ${guide.steps.length} step${guide.steps.length === 1 ? '' : 's'}? It's gone for everyone once you save.`)) return;
              const i = draft.findIndex((g) => g.id === guide.id);
              const list = removeGuide(draft, guide.id);
              setDraft(list); setTab(list[Math.max(0, i - 1)]!.id);
            }}>Delete this guide</button>
          <span className="hint">Rename a guide with <b>Tab name</b> below.</span>
        </div>
      )}
      {loading ? <p className="hint">Loading the guide…</p>
        : draft ? <GuideEditor key={guide.id} guide={guide} onChange={(g) => setDraft(draft.map((x) => (x.id === g.id ? g : x)))} />
          : <GuideView key={guide.id} guide={guide} />}
    </Dialog>
  );
}
